import { useCallback, useEffect, useRef, useState } from 'react'

export type RecorderStatus = 'idle' | 'recording' | 'denied' | 'error'

export type VoiceClip = { blob: Blob; durationMs: number; filename: string }

/**
 * MediaRecorder voice notes. iOS Safari < 18.4 records `audio/mp4`; the api derives `kind:"voice"`
 * from whatever audio mime arrives (docs/CONTRACT.md §8), so the mime is negotiated, not assumed.
 */
export function useVoiceRecorder() {
  const [status, setStatus] = useState<RecorderStatus>('idle')
  const [seconds, setSeconds] = useState(0)
  const [level, setLevel] = useState(0)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  // `window.setInterval` rather than the bare global: the handle is a number in the browser, and
  // the bare global is typed by whichever runtime's types are loaded (Bun/Node call it a `Timeout`).
  const sampleTimerRef = useRef<number | null>(null)

  const release = useCallback(() => {
    if (sampleTimerRef.current !== null) {
      clearInterval(sampleTimerRef.current)
      sampleTimerRef.current = null
    }
    void audioContextRef.current?.close().catch(() => undefined)
    audioContextRef.current = null
    analyserRef.current = null
    for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    streamRef.current = null
    recorderRef.current = null
  }, [])

  useEffect(() => release, [release])

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
      streamRef.current = stream
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : ''
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      chunksRef.current = []
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      })
      recorderRef.current = recorder
      startedAtRef.current = Date.now()
      setSeconds(0)
      setStatus('recording')
      recorder.start()

      const audioContext = new AudioContext()
      audioContextRef.current = audioContext
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 512
      analyserRef.current = analyser
      audioContext.createMediaStreamSource(stream).connect(analyser)
      const samples = new Uint8Array(analyser.fftSize)
      sampleTimerRef.current = window.setInterval(() => {
        const node = analyserRef.current
        if (!node) return
        node.getByteTimeDomainData(samples)
        let peak = 0
        for (const value of samples) peak = Math.max(peak, Math.abs(value - 128) / 128)
        setLevel(peak)
        setSeconds(Math.round((Date.now() - startedAtRef.current) / 1000))
      }, 100)
    } catch {
      release()
      setStatus('denied')
    }
  }, [release])

  const stop = useCallback(async (): Promise<VoiceClip | null> => {
    const recorder = recorderRef.current
    if (!recorder) return null
    const durationMs = Date.now() - startedAtRef.current
    const blob = await new Promise<Blob | null>((resolve) => {
      recorder.addEventListener('stop', () => {
        resolve(chunksRef.current.length > 0 ? new Blob(chunksRef.current, { type: recorder.mimeType }) : null)
      })
      recorder.stop()
    })
    release()
    setStatus('idle')
    setLevel(0)
    setSeconds(0)
    if (!blob) return null
    const extension = blob.type.includes('mp4') ? 'm4a' : 'webm'
    return { blob, durationMs, filename: `voice-${Date.now()}.${extension}` }
  }, [release])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    chunksRef.current = []
    release()
    setStatus('idle')
    setLevel(0)
    setSeconds(0)
  }, [release])

  return { status, seconds, level, start, stop, cancel }
}
